import { t } from '../../../lib/i18n';
import type { FinanceClient } from '../types';
import { cn } from '../utils';
import { CLIENT_COLUMNS, FinanceClientRow } from './finance-client-row';
import { PanelEmpty } from './panel-state';

interface FinanceClientsTableProps {
  clients: FinanceClient[];
  /**
   * What "no rows" means right now. An empty PLATFORM (nobody has been
   * invoiced yet — the normal first-run state) and an empty FILTER (a search
   * the operator can undo) are different facts, and a table that says the same
   * sentence for both turns a working first day into a support ticket.
   */
  emptyKey: string;
  /** Which rows are expanded, by `userId`. */
  expanded: Record<string, boolean>;
  onToggle: (userId: string) => void;
}

/**
 * The balances table — `Redesign-D-Admin`, «Фінанси».
 *
 * On desktop this is a real table: a sticky-width grid that scrolls sideways
 * below 960px rather than reflowing, because the seven money columns only mean
 * anything while they stay in line with each other. On mobile the wrapper
 * disappears entirely and the rows become free-standing cards — hence the
 * `lg:` border and background here rather than on every row.
 *
 * Rows are keyed by `userId` rather than by name: two clients can share a
 * display name (the rollup falls back to the email, then to the id, when a user
 * record carries none), and a duplicate React key would collapse one of them.
 */
export function FinanceClientsTable({
  clients,
  emptyKey,
  expanded,
  onToggle,
}: FinanceClientsTableProps) {
  if (!clients.length) return <PanelEmpty messageKey={emptyKey} />;

  return (
    <section className="flex flex-col gap-2 lg:gap-0 lg:overflow-x-auto lg:rounded-cf-lg lg:border lg:border-cf-line lg:bg-cf-surface">
      <div
        className={cn(
          'hidden min-w-[960px] border-b border-cf-line-soft py-2.5 font-mono text-[10px] tracking-[0.12em] text-cf-ink-subtle uppercase lg:grid',
          CLIENT_COLUMNS,
        )}
      >
        <span>{t('adminFinance.table.client')}</span>
        {/* The header names the CURRENCY, because this column alone is CHF. */}
        <span className="text-right">{t('adminFinance.table.depositChf')}</span>
        <span className="text-right">{t('adminFinance.table.invoiced')}</span>
        <span className="text-right">{t('adminFinance.table.paid')}</span>
        <span className="text-right">{t('adminFinance.table.owed')}</span>
        <span>{t('adminFinance.table.status')}</span>
        <span className="text-right">{t('adminFinance.table.action')}</span>
      </div>

      {clients.map((client) => (
        <FinanceClientRow
          key={client.userId}
          client={client}
          expanded={Boolean(expanded[client.userId])}
          onToggle={() => onToggle(client.userId)}
        />
      ))}
    </section>
  );
}
